import { useMemo, useState } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { services, staff, appointments as seedAppts } from "../lib/mockData";
import { CalendarDays, Check, ChevronRight, Clock, Mail, MessageCircle, RotateCcw, User2, X } from "lucide-react";
import { toast } from "sonner";
import BeforeAfter from "../components/common/BeforeAfter";
import ConsultationCTA from "../components/common/ConsultationCTA";

const HOURS = ["09:00","10:00","11:00","12:00","13:00","14:00","15:00","16:00","17:00"];

const DemoHeader = () => (
  <div className="mb-8">
    <div className="text-xs font-semibold uppercase tracking-widest text-primary">Demo 3</div>
    <h1 className="mt-1 font-display text-3xl sm:text-4xl lg:text-5xl leading-tight">Appointment Booking</h1>
    <p className="mt-3 text-muted-foreground text-lg max-w-3xl">
      A modern booking flow with staff selection, real-time availability, calendar view and one-tap reschedule.
    </p>
  </div>
);

const Step = ({ n, active, done, label }) => (
  <div className="flex items-center gap-2">
    <div className={`h-7 w-7 rounded-full grid place-items-center text-xs font-bold
      ${done ? "bg-accent text-accent-foreground" : active ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
      {done ? <Check className="w-3.5 h-3.5"/> : n}
    </div>
    <span className={`text-sm ${active || done ? "font-semibold" : "text-muted-foreground"}`}>{label}</span>
  </div>
);

const nextNDays = (n = 7) => {
  const out = [];
  const d = new Date();
  for (let i = 0; i < n; i++) {
    const c = new Date(d); c.setDate(d.getDate() + i);
    out.push({ iso: c.toISOString().slice(0,10), label: c.toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short" }) });
  }
  return out;
};

const BookingFlow = ({ onBook }) => {
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [service, setService] = useState(services[0].id);
  const [staffId, setStaffId] = useState(staff[0].id);
  const [date, setDate] = useState(nextNDays()[1].iso);
  const [time, setTime] = useState("10:00");
  const days = useMemo(() => nextNDays(), []);

  const svc = services.find((s) => s.id === service);
  const stf = staff.find((s) => s.id === staffId);

  const finalize = () => {
    onBook({
      id: `nb-${Date.now()}`, client: name || "New client", service: svc.name,
      staff: stf.name, date, time, status: "Confirmed",
    });
    toast.success("Appointment confirmed. Email & WhatsApp sent.");
    setStep(1); setName(""); setPhone("");
  };

  return (
    <Card className="glass"><CardContent className="p-6 space-y-6">
      <div className="flex flex-wrap items-center gap-3 gap-y-2">
        <Step n={1} label="Service" done={step > 1} active={step === 1}/>
        <ChevronRight className="w-4 h-4 text-muted-foreground"/>
        <Step n={2} label="Staff" done={step > 2} active={step === 2}/>
        <ChevronRight className="w-4 h-4 text-muted-foreground"/>
        <Step n={3} label="Date & time" done={step > 3} active={step === 3}/>
        <ChevronRight className="w-4 h-4 text-muted-foreground"/>
        <Step n={4} label="Confirm" active={step === 4}/>
      </div>

      {step === 1 && (
        <div className="space-y-3">
          <Label>Choose a service</Label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {services.map((s) => (
              <button key={s.id} onClick={() => setService(s.id)} data-testid={`svc-${s.id}`}
                className={`text-left rounded-xl border p-4 transition-colors duration-300 ${service === s.id ? "border-primary bg-primary/5" : "border-border hover:bg-muted/60"}`}>
                <div className="font-medium">{s.name}</div>
                <div className="text-xs text-muted-foreground mt-1">{s.duration} min · GHS ₵{s.price}</div>
              </button>
            ))}
          </div>
          <Button onClick={() => setStep(2)} data-testid="booking-next-1">Continue</Button>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-3">
          <Label>Pick a staff member</Label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {staff.map((s) => (
              <button key={s.id} onClick={() => setStaffId(s.id)} data-testid={`staff-${s.id}`}
                className={`text-left rounded-xl border p-4 flex items-center gap-3 transition-colors duration-300 ${staffId === s.id ? "border-primary bg-primary/5" : "border-border hover:bg-muted/60"}`}>
                <div className="h-10 w-10 rounded-full bg-primary text-primary-foreground grid place-items-center font-bold">
                  {s.name.split(" ").map((p) => p[0]).slice(0,2).join("")}
                </div>
                <div>
                  <div className="font-medium">{s.name}</div>
                  <div className="text-xs text-muted-foreground">{s.role} · ★ {s.rating}</div>
                </div>
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
            <Button onClick={() => setStep(3)} data-testid="booking-next-2">Continue</Button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          <div>
            <Label>Choose a date</Label>
            <div className="mt-2 flex gap-2 overflow-x-auto pb-1">
              {days.map((d) => (
                <button key={d.iso} onClick={() => setDate(d.iso)} data-testid={`day-${d.iso}`}
                  className={`px-3 py-2 rounded-lg text-xs border whitespace-nowrap transition-colors duration-300 ${date === d.iso ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}>
                  {d.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label>Choose a time</Label>
            <div className="mt-2 grid grid-cols-3 sm:grid-cols-5 gap-2">
              {HOURS.map((h) => (
                <button key={h} onClick={() => setTime(h)} data-testid={`time-${h}`}
                  className={`px-3 py-2 rounded-lg text-sm border transition-colors duration-300 ${time === h ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}>
                  {h}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Your name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="booking-name"/></div>
            <div><Label>Phone</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="booking-phone"/></div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setStep(2)}>Back</Button>
            <Button onClick={() => setStep(4)} data-testid="booking-next-3">Review</Button>
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="space-y-4">
          <div className="rounded-xl bg-primary/5 border border-primary/20 p-4 space-y-1">
            <div className="text-xs uppercase tracking-widest text-primary">Confirmation</div>
            <div className="text-lg font-display font-semibold">{svc.name} with {stf.name}</div>
            <div className="text-sm text-muted-foreground flex items-center gap-2"><CalendarDays className="w-4 h-4"/> {date} · <Clock className="w-4 h-4"/> {time}</div>
            <div className="text-sm text-muted-foreground flex items-center gap-2"><User2 className="w-4 h-4"/> {name || "You"}</div>
          </div>
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/40"><Mail className="w-4 h-4 text-accent"/> Email confirmation ready</div>
            <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/40"><MessageCircle className="w-4 h-4 text-accent"/> WhatsApp reminder queued</div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setStep(3)}>Back</Button>
            <Button onClick={finalize} data-testid="booking-confirm">Confirm booking</Button>
          </div>
        </div>
      )}
    </CardContent></Card>
  );
};

const CalendarView = ({ list, onCancel, onReschedule }) => {
  const grouped = list.reduce((acc, a) => { (acc[a.date] ||= []).push(a); return acc; }, {});
  const dates = Object.keys(grouped).sort();
  return (
    <Card className="glass"><CardContent className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-primary">Admin view</div>
          <div className="font-display text-lg font-semibold">Upcoming appointments</div>
        </div>
        <Badge variant="secondary">{list.length} total</Badge>
      </div>
      <div className="space-y-5">
        {dates.map((d) => (
          <div key={d}>
            <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">
              {new Date(d).toLocaleDateString("en-GB", { weekday: "long", day: "2-digit", month: "long" })}
            </div>
            <div className="space-y-2">
              {grouped[d].sort((a,b) => a.time.localeCompare(b.time)).map((a) => (
                <div key={a.id} className="flex flex-wrap items-center gap-3 border border-border rounded-xl p-3">
                  <div className="text-sm font-bold w-14">{a.time}</div>
                  <div className="flex-1 min-w-[180px]">
                    <div className="text-sm font-medium">{a.client}</div>
                    <div className="text-xs text-muted-foreground">{a.service} · {a.staff}</div>
                  </div>
                  <Badge className={a.status === "Confirmed" ? "bg-accent text-accent-foreground" : "bg-primary/10 text-primary"}>{a.status}</Badge>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => onReschedule(a)} data-testid={`resched-${a.id}`}><RotateCcw className="w-3.5 h-3.5"/></Button>
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={() => onCancel(a.id)} data-testid={`cancel-${a.id}`}><X className="w-3.5 h-3.5"/></Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </CardContent></Card>
  );
};

export default function Demo3Appointments() {
  const [list, setList] = useState(seedAppts);

  const addBooking = (a) => setList((L) => [...L, a]);
  const cancel = (id) => { setList((L) => L.filter((a) => a.id !== id)); toast("Appointment cancelled"); };
  const reschedule = (a) => {
    const newTime = HOURS[(HOURS.indexOf(a.time) + 1) % HOURS.length];
    setList((L) => L.map((x) => x.id === a.id ? { ...x, time: newTime } : x));
    toast.success(`Rescheduled ${a.client} to ${newTime}`);
  };

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14">
      <DemoHeader/>
      <Tabs defaultValue="book" className="w-full">
        <TabsList data-testid="appts-tabs">
          <TabsTrigger value="book" data-testid="tab-book">Book</TabsTrigger>
          <TabsTrigger value="calendar" data-testid="tab-calendar">Admin calendar</TabsTrigger>
        </TabsList>
        <TabsContent value="book"><div className="max-w-3xl"><BookingFlow onBook={addBooking}/></div></TabsContent>
        <TabsContent value="calendar"><CalendarView list={list} onCancel={cancel} onReschedule={reschedule}/></TabsContent>
      </Tabs>

      <BeforeAfter demoId={3}/>
      <ConsultationCTA demoLabel="Appointment Booking"/>
    </div>
  );
}
